/**
 * TypeScript interfaces and types for Organization feature
 */

import { getMeantToPlatforms } from './index';

export interface OrganizationTab {
  tab_id: string;
  tab_name: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  display_order: number;
  entity_type: 'organization_tab';
  lists?: OrganizationList[];
}

export interface OrganizationList {
  list_id: string;
  tab_id: string;
  list_name: string;
  is_default: boolean;
  created_at: string;
  created_by: string;
  updated_at: string;
  display_order: number;
  entity_type: 'organization_list';
  ad_count: number;
}

export interface OrganizationListItem {
  list_id: string;
  ad_id: string;
  tab_id: string;
  position: number;
  added_at: string;
  added_by: string;
  entity_type: 'organization_list_item';
}

export interface AdInList {
  ad_id: string;
  ad_name: string;
  thumbnail_url?: string;
  position: number;
  upload_status?: {
    meta: 'uploaded' | 'not_uploaded' | 'pending';
    youtube: 'uploaded' | 'not_uploaded' | 'pending';
  };
  ad_page_url: string;
  added_at?: string;
  // Additional ad fields
  creative_number?: number | string;
  production_id?: number | string;
  ratio?: string;
  video_url?: string;
  duration?: number | string;
  // Upload status fields (like AdsTablePage)
  youtube_video_id?: string;
  meta_ad_id?: string;
  meta_ad_ids?: Record<string, string>;
  // Metadata for thumbnail generation
  metadata?: {
    file_metadata?: {
      content_type?: string;
    };
    media_type?: string;
    [key: string]: any;
  };
}

export interface AdSearchResult {
  ad_id: string;
  ad_name: string;
  thumbnail_url: string;
  upload_status: {
    meta: 'uploaded' | 'not_uploaded' | 'pending';
    youtube: 'uploaded' | 'not_uploaded' | 'pending';
  };
  already_in_tab: boolean;
  ad_page_url: string;
}

// API Request/Response Types
export interface CreateTabRequest {
  tab_name: string;
}

export interface UpdateTabRequest {
  tab_name?: string;
  display_order?: number;
}

export interface ReorderTabsRequest {
  tab_orders: Array<{
    tab_id: string;
    display_order: number;
  }>;
}

export interface CreateListRequest {
  list_name: string;
}

export interface UpdateListRequest {
  list_name?: string;
  display_order?: number;
}

export interface AddAdsToListRequest {
  ad_ids: string[];
}

export interface MoveAdRequest {
  target_list_id: string;
  position?: number | null;
}

export interface ReorderAdsRequest {
  ad_positions: Array<{
    ad_id: string;
    position: number;
  }>;
}

// API Response Types
export interface GetAllTabsResponse {
  tabs: OrganizationTab[];
}

export interface GetListsResponse {
  lists: OrganizationList[];
}

export interface GetListAdsResponse {
  ads: AdInList[];
}

export interface SearchAdsResponse {
  results: AdSearchResult[];
  count: number;
}

// UI State Types
export interface DraggedItem {
  ad: AdInList;
  sourceListId: string;
}

export interface UndoAction {
  type: 'move_ad';
  ad_id: string;
  source_list_id: string;
  target_list_id: string;
  original_position: number;
}

export interface OrganizationState {
  tabs: OrganizationTab[];
  activeTabId: string | null;
  lists: Record<string, OrganizationList[]>; // Keyed by tab_id
  ads: Record<string, AdInList[]>; // Keyed by list_id
  isLoading: boolean;
  error: string | null;
  undoAction: UndoAction | null;
}

// ==================== RULE TYPES ====================

export interface RuleCondition {
  field: string;        // Field to check (e.g., 'meantTo', 'created_by', 'upload_status.meta')
  operator: RuleOperator;  // Comparison operator
  value?: any;          // Value to compare against (optional for is_null/is_not_null)
}

export type RuleOperator = 
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'one_of'
  | 'is_null'
  | 'is_not_null'
  | 'greater_than'
  | 'less_than';

export interface OrganizationRule {
  rule_id: string;
  list_id: string;
  tab_id: string;
  rule_name: string;
  conditions: RuleCondition[];
  created_at: string;
  created_by: string;
  updated_at: string;
  last_applied_at?: string;
  last_applied_count?: number;
  entity_type: 'organization_rule';
}

export interface CreateRuleRequest {
  rule_name: string;
  conditions: RuleCondition[];
}

export interface UpdateRuleRequest {
  rule_name?: string;
  conditions?: RuleCondition[];
}

export interface ApplyRuleResponse {
  ads_added: number;
  applied_at: string;
}

export interface PreviewRuleRequest {
  conditions: RuleCondition[];
  limit?: number;
}

export interface PreviewRuleResponse {
  matching_ads_count: number;
  sample_ads: AdInList[];
}

export interface GetRulesResponse {
  rules: OrganizationRule[];
}

// Field options for rule conditions
export const RULE_FIELD_OPTIONS = [
  { value: 'ad_id', label: 'Ad ID', type: 'string' },
  { value: 'metadata.tags.meantTo', label: 'Meant To', type: 'array' },
  { value: 'created_by', label: 'Creator', type: 'string' },
  { value: 'upload_status.meta', label: 'Meta Upload Status', type: 'status' },
  { value: 'upload_status.youtube', label: 'YouTube Upload Status', type: 'status' },
  { value: 'metadata.tags.cast', label: 'Cast', type: 'string' },
  { value: 'metadata.tags.heroAge', label: 'Hero Age', type: 'string' },
  { value: 'metadata.tags.heroGender', label: 'Hero Gender', type: 'string' },
  { value: 'metadata.tags.pianoType', label: 'Piano Type', type: 'string' },
  { value: 'metadata.tags.appDevice', label: 'App Device', type: 'string' },
  { value: 'metadata.tags.licensedSong', label: 'Licensed Song', type: 'array' },
  { value: 'metadata.usps.usp_list', label: 'USP', type: 'array' },
  { value: 'metadata.auto_tagging.visual_usps', label: 'Visual-based USP', type: 'array' },
  { value: 'production_id', label: 'Production ID', type: 'number' },
  { value: 'ratio', label: 'Ratio', type: 'string' },
  { value: 'language', label: 'Language', type: 'string' },
] as const;

// Operator options for rule conditions
export const RULE_OPERATOR_OPTIONS: Array<{
  value: RuleOperator;
  label: string;
  requiresValue: boolean;
  compatibleTypes: string[];
}> = [
  { value: 'equals', label: 'equals', requiresValue: true, compatibleTypes: ['string', 'number'] },
  { value: 'not_equals', label: 'not equals', requiresValue: true, compatibleTypes: ['string', 'number'] },
  { value: 'contains', label: 'contains', requiresValue: true, compatibleTypes: ['array', 'string'] },
  { value: 'not_contains', label: 'does not contain', requiresValue: true, compatibleTypes: ['array', 'string'] },
  { value: 'one_of', label: 'is one of', requiresValue: true, compatibleTypes: ['string', 'array'] },
  { value: 'is_null', label: 'is not set', requiresValue: false, compatibleTypes: ['string', 'number', 'array', 'status'] },
  { value: 'is_not_null', label: 'is set', requiresValue: false, compatibleTypes: ['string', 'number', 'array', 'status'] },
  { value: 'greater_than', label: 'greater than', requiresValue: true, compatibleTypes: ['number'] },
  { value: 'less_than', label: 'less than', requiresValue: true, compatibleTypes: ['number'] },
];

// Predefined options for fields with finite values
export const FIELD_PREDEFINED_OPTIONS: Record<string, string[]> = {
  'metadata.tags.cast': ['Kids', 'Couple', 'Solo actor', 'No Human actor', 'Human with Prompt', 'hand only', 'Family', 'unknown'],
  'metadata.tags.heroAge': ['Senior', 'Adult', 'Teenage', 'Kid', 'unknown'],
  'metadata.tags.heroGender': ['Female', 'Male', 'other', 'unknown'],
  'metadata.tags.pianoType': ['Piano', 'Keyboard', 'Mixed', 'No instrument', 'unknown'],
  'metadata.tags.appDevice': ['phone', 'tablet', 'both', 'no device', 'unknown'],
  'metadata.tags.meantTo': getMeantToPlatforms(),
  'metadata.usps.usp_list': [
    'Own pace, own time',
    'Easy to follow bite-size courses',
    'Takes you from beginner to pro',
    'Teaches you to read notes',
    'Works with any piano or keyboard',
    'Works on any device',
    'Real-time feedback',
    'Has a variety of songs to play to',
    'Personalized',
    'Track your progress',
    'AI-based technology',
    'Gamified',
    'Makes piano learning fun',
    'Top Rated app',
    'Millions of happy users',
    'Developed by music teachers',
    'Perfect for all ages',
    'A strong community',
    'College certificate',
    'Affordable',
    'Family Plan (multi-app/multi-profile)',
    'Free trial',
    'Learn fast',
    'Both hands',
    'Other',
    'Pause and rewind',
    'Control song speed',
  ],
  'metadata.auto_tagging.visual_usps': [
    'Own pace, own time',
    'Easy to follow bite-size courses',
    'Takes you from beginner to pro',
    'Teaches you to read notes',
    'Works with any piano or keyboard',
    'Works on any device',
    'Real-time feedback',
    'Has a variety of songs to play to',
    'Personalized',
    'Track your progress',
    'AI-based technology',
    'Gamified',
    'Makes piano learning fun',
    'Top Rated app',
    'Millions of happy users',
    'Developed by music teachers',
    'Perfect for all ages',
    'A strong community',
    'College certificate',
    'Affordable',
    'Family Plan (multi-app/multi-profile)',
    'Free trial',
    'Learn fast',
    'Both hands',
    'Other',
    'Pause and rewind',
    'Control song speed',
  ],
  'ratio': ['16:9', '9:16', '1:1', '4:3', '4:5', '21:9'],
  'language': ['EN', 'ES', 'FR', 'DE', 'IT', 'PT', 'PT-BR', 'NL', 'PL', 'RU', 'JA', 'KO', 'ZH', 'AR', 'HI', 'SV', 'TR'],
  'upload_status.meta': ['uploaded', 'not_uploaded', 'pending'],
  'upload_status.youtube': ['uploaded', 'not_uploaded', 'pending'],
};

